/**
 * Why the app is showing the train it is showing.
 *
 * Every pin change had exactly one diagnostic — a plan id — so a swipe, a tap on the board and the
 * app repairing its own state all wrote the same row. On 2026-09-15 the pin moved from the train he
 * had just boarded to one that had left nine minutes earlier, and the telemetry could not say
 * whether a finger did it. The cause is the fact worth keeping: a train he chose is a decision, and
 * the same train chosen by an effect is a bug.
 */

export const PIN_ORIGINS = ["swipe", "board-tap", "watch-tap", "departed-train", "restore", "repair", "replan", "reset"] as const;
export type PinOrigin = (typeof PIN_ORIGINS)[number];

/** A finger on the glass. Everything else is the app deciding for itself. */
const USER_ORIGINS: readonly PinOrigin[] = ["swipe", "board-tap", "watch-tap"];

export function pinByUser(origin: PinOrigin): boolean {
	return USER_ORIGINS.includes(origin);
}

export interface PinChange {
	origin: PinOrigin;
	fromId: string | null;
	toId: string | null;
	recommendedId?: string | null;
	/** What the phone's own record says he is on, which is not always what is pinned. */
	guessedId?: string | null;
	boarded?: boolean;
}

export interface PinChangeRecord extends PinChange {
	byUser: boolean;
	changed: boolean;
	/**
	 * Aboard, nothing but he should be able to move the pin: the recommendation is a train he is
	 * not on, and a repair that fires here rewrites which train he is riding. This is the flag to
	 * search for when the card names the wrong one.
	 */
	movedItselfAboard: boolean;
	agreesWithGuess: boolean | null;
	agreesWithRecommendation: boolean | null;
}

export function pinChange(change: PinChange): PinChangeRecord {
	const byUser = pinByUser(change.origin);
	const changed = change.fromId !== change.toId;
	return {
		...change,
		byUser,
		changed,
		movedItselfAboard: changed && !byUser && change.boarded === true,
		agreesWithGuess: change.guessedId == null || change.toId == null ? null : change.guessedId === change.toId,
		agreesWithRecommendation:
			change.recommendedId == null || change.toId == null ? null : change.recommendedId === change.toId,
	};
}
